/**
 * LaTeX Parser — Shorthand to LaTeX transformer
 * Per PLAN.md latex/Parser.js and PRD §6.3
 */

// Greek letter mappings
const GREEK_LETTERS = {
    'alpha': '\\alpha',
    'beta': '\\beta',
    'gamma': '\\gamma',
    'delta': '\\delta',
    'epsilon': '\\epsilon',
    'zeta': '\\zeta',
    'eta': '\\eta',
    'theta': '\\theta',
    'iota': '\\iota',
    'kappa': '\\kappa',
    'lambda': '\\lambda',
    'mu': '\\mu',
    'nu': '\\nu',
    'xi': '\\xi',
    'pi': '\\pi',
    'rho': '\\rho',
    'sigma': '\\sigma',
    'tau': '\\tau',
    'upsilon': '\\upsilon',
    'phi': '\\phi',
    'chi': '\\chi',
    'psi': '\\psi',
    'omega': '\\omega',
    // Uppercase
    'Alpha': '\\Alpha',
    'Beta': '\\Beta',
    'Gamma': '\\Gamma',
    'Delta': '\\Delta',
    'Theta': '\\Theta',
    'Lambda': '\\Lambda',
    'Pi': '\\Pi',
    'Sigma': '\\Sigma',
    'Phi': '\\Phi',
    'Psi': '\\Psi',
    'Omega': '\\Omega'
};

// Operator replacements per PRD §6.3
const OPERATORS = {
    '>=': '\\geq',
    '<=': '\\leq',
    '!=': '\\neq',
    '+-': '\\pm',
    '-+': '\\mp',
    '...': '\\ldots',
    '->': '\\rightarrow',
    '<-': '\\leftarrow',
    '<->': '\\leftrightarrow',
    '=>': '\\Rightarrow',
    'inf': '\\infty'
};

// Function names that need \
const FUNCTIONS = ['log', 'ln', 'sin', 'cos', 'tan', 'sec', 'csc', 'cot',
    'arcsin', 'arccos', 'arctan', 'sinh', 'cosh', 'tanh',
    'lim', 'max', 'min', 'sup', 'inf', 'det', 'exp'];

/**
 * Parse shorthand notation to proper LaTeX
 * @param {string} input - User input
 * @returns {string} LaTeX string
 */
export function parseLatex(input) {
    if (!input || input.trim() === '') return '';

    let result = input;

    // Replace operators first (before they get broken by other transforms)
    result = replaceOperators(result);

    // Replace Greek letters (as whole words)
    result = replaceGreekLetters(result);

    // Handle sqrt(...)
    result = parseRoots(result);

    // Handle fractions: a/b or (a+b)/(c+d)
    result = parseFractions(result);

    // Handle subscripts: x_1 or log_10
    result = parseSubscripts(result);

    // Handle superscripts: x^2 or e^{-x}
    result = parseSuperscripts(result);

    // Handle function names
    result = parseFunctions(result);

    // Replace * with \cdot
    result = result.replace(/\*/g, '\\cdot ');

    return result;
}

/**
 * Replace operator shortcuts
 */
function replaceOperators(input) {
    let result = input;

    for (const [short, latex] of Object.entries(OPERATORS)) {
        // Escape special regex characters
        const escaped = short.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        result = result.replace(new RegExp(escaped, 'g'), latex);
    }

    return result;
}

/**
 * Replace Greek letter names with LaTeX commands
 */
function replaceGreekLetters(input) {
    let result = input;

    // Sort by length (longest first) to avoid partial replacements
    const sorted = Object.keys(GREEK_LETTERS).sort((a, b) => b.length - a.length);

    for (const letter of sorted) {
        // Word boundary to avoid replacing inside other words
        const regex = new RegExp(`\\b${letter}\\b`, 'g');
        result = result.replace(regex, GREEK_LETTERS[letter]);
    }

    return result;
}

/**
 * Parse sqrt(x) to \sqrt{x}
 */
function parseRoots(input) {
    // Handle sqrt(...) with nested parentheses
    return input.replace(/sqrt\(([^)]+)\)/g, '\\sqrt{$1}');
}

/**
 * Parse fractions: a/b or (expr)/(expr)
 */
function parseFractions(input) {
    let result = input;

    // Match (...)/(...)
    result = result.replace(/\(([^)]+)\)\/\(([^)]+)\)/g, '\\frac{$1}{$2}');

    // Match simple a/b (single terms)
    // More conservative: only match simple terms without spaces
    result = result.replace(/(\w+)\/(\w+)/g, (match, num, den) => {
        // Don't convert if it looks like it's already in a frac or if too complex
        if (match.includes('frac')) return match;
        return `\\frac{${num}}{${den}}`;
    });

    return result;
}

/**
 * Parse subscripts: x_1, x_10, log_10
 */
function parseSubscripts(input) {
    // Match letter(s)_something where something is digits or single letter
    return input.replace(/([a-zA-Z]+)_([a-zA-Z0-9]+)/g, (match, base, sub) => {
        // Check if it's a function that needs special handling
        if (FUNCTIONS.includes(base.toLowerCase())) {
            return `\\${base.toLowerCase()}_{${sub}}`;
        }
        // Wrap subscript in braces if more than one character
        if (sub.length > 1) {
            return `${base}_{${sub}}`;
        }
        return `${base}_${sub}`;
    });
}

/**
 * Parse superscripts: x^2, x^{-1}
 */
function parseSuperscripts(input) {
    let result = input;

    // Match x^{...} (already braced)
    // No change needed

    // Match x^(something) -> x^{something}
    result = result.replace(/\^{?\(([^)]+)\)}?/g, '^{$1}');

    // Match x^2 or x^12 (numbers)
    result = result.replace(/\^(\d+)/g, (match, exp) => {
        if (exp.length > 1) {
            return `^{${exp}}`;
        }
        return match;
    });

    // Match x^-n (negative exponents)
    result = result.replace(/\^(-\d+)/g, '^{$1}');

    return result;
}

/**
 * Parse function names: log, sin, cos, etc.
 */
function parseFunctions(input) {
    let result = input;

    for (const fn of FUNCTIONS) {
        // Match function name at word boundary, not already escaped
        const regex = new RegExp(`(?<!\\\\)\\b${fn}\\b`, 'gi');
        result = result.replace(regex, `\\${fn.toLowerCase()}`);
    }

    return result;
}

/**
 * Check if input is valid LaTeX (basic check)
 * @param {string} input 
 * @returns {boolean}
 */
export function isValidLatex(input) {
    // Check for balanced braces
    let braceCount = 0;
    for (const char of input) {
        if (char === '{') braceCount++;
        if (char === '}') braceCount--;
        if (braceCount < 0) return false;
    }
    return braceCount === 0;
}
